import {
  Discount,
  DiscountUserInputV1,
  DiscountUserInputV2,
  DiscountUserInputV3,
  DiscountPricingConfig
} from './DiscountTypes'
import type { FreeDropinTicketProvenance, FreeDropinTicketRule } from './FreeDropinTicketTypes'
import { EmailService } from './MailingTypes'
import { StripeEnvironment } from './StripeTypes'
import { SwishConfigEnvironment } from './SwishTypes'
import { BaseObject } from './types'
import { VippsConfigEnvironment } from './VippsMobileTypes'
import { ClientFortnoxConfig } from './FortnoxTypes'
import { ClientSebConfig } from './SebTypes'
import { ClientEpassiConfig } from './EpassiTypes'
import { CommunicationLogEvent } from './CommunicationLogTypes'
import type { Ocr } from './OcrTypes'

// Add import for DanceRoles

// Define VAT rates
export type VATRate = 0 | 6 | 12 | 25

export type InternalCustomerData = BaseObject & {
  clientId: string
  stripeCustomerId: string
}

export type AdminDoc = {
  id: string // ie. releaseNotes
  data: string // JSON or HTML or anything You like
  createdAt: string // timestamp
  updatedAt: string // timestamp
  updatedBy?: string // optional (who updated last)
}

export enum PaymentMethod {
  Stripe = 'Stripe',
  StripeDropin = 'StripeDropin',
  StripePrivateLesson = 'StripePrivateLesson',
  BankTransfer = 'BankTransfer',
  Swish = 'Swish',
  SwishManual = 'SwishManual',
  ManualCard = 'ManualCard',
  Cash = 'Cash',
  Klarna = 'Klarna',
  UserCredit = 'UserCredit',
  VippsMobile = 'VippsMobile',
  VippsMobileManual = 'VippsMobileManual',
  Epassi = 'Epassi',
  Fritidskortet = 'Fritidskortet',
  Manual = 'manual'
}

export enum PaymentOperator {
  Coursely = 'coursely',
  Kursanmalan = 'kursanmalan'
}

export enum PaymentSource {
  Stripe = 'stripe',
  Swish = 'swish',
  SwishWalkIn = 'swish_walkin',
  StripeWalkIn = 'stripe_walkin',
  /**
   * Card-present payment taken on a client's Stripe Terminal S700 against an **existing**
   * enrollment, via Reception's Trigger Pay (COU-325).
   *
   * Distinct from its two neighbours:
   * - `Stripe` — online, card-not-present, customer pays via checkout.
   * - `StripeWalkIn` — also card-present on the same reader, but *creates a new enrollment*
   *   and auto-checks the attendee in. This one only appends a payment to an enrollment
   *   that already exists, and never checks anyone in.
   *
   * Being a gateway-captured payment, it must be refundable and must NOT be deletable —
   * see `isGatewayPayment` / `findRefundableStripePayments` in PaymentHelper.ts.
   */
  StripeTerminal = 'stripe_terminal',
  UserCredit = 'usercredit',
  Manual = 'manual',
  Vipps = 'vipps',
  Fortnox = 'fortnox',
  Seb = 'seb',
  Fritidskortet = 'fritidskortet',
  Epassi = 'epassi'
}

// Convert string literal types to proper enums
export enum GroupByOption {
  Style = 'style',
  Type = 'type',
  City = 'city',
  StartDate = 'startdate',
  Status = 'status',
  None = 'none',
  Period = 'period',
  Instructors = 'instructors',
  Level = 'level',
  Location = 'location'
}

export enum ActivityStatus {
  Active = 'active',
  Draft = 'draft',
  Planned = 'planned',
  Cancelled = 'cancelled',
  Completed = 'completed',
  Archived = 'archived',
  Deleted = 'deleted'
}

export enum ActivityRegistrationStatus {
  Open = 'open',
  Full = 'full',
  Closed = 'closed',
  ClosedForLeaders = 'closedForLeaders',
  ClosedForFollowers = 'closedForFollowers',
  Special = 'special'
}

export enum EnrollmentStatus {
  Pending = 'pending',
  Confirmed = 'confirmed',
  Active = 'active',
  Cancelled = 'cancelled',
  Deleted = 'deleted',
  /** @deprecated Prefer CancelledWithRefund. Kept for reading legacy docs. */
  Refunded = 'refunded',
  PartlyApproved = 'partlyApproved',
  ClassCancelled = 'classCancelled',
  CancelledWithRefund = 'cancelledWithRefund',
  CancelledWithCredit = 'cancelledWithCredit'
}

export enum PeriodStatus {
  Active = 'active',
  Draft = 'draft',
  Archived = 'archived'
}

/** Sort options for the activity Check-ins list (admin). */
export enum CheckinsListSortBy {
  Name = 'name',
  CreationDate = 'creationDate',
  CheckinTime = 'checkinTime',
  State = 'state'
}

/** Ascending vs descending for `CheckinsListSortBy`. */
export enum CheckinsListSortDirection {
  Asc = 'asc',
  Desc = 'desc'
}

/** Participant sort on Startsida today’s check-in (under Search). */
export enum StartsidaCheckinSort {
  Alphabetical = 'alphabetical',
  CheckinStatus = 'checkinStatus'
}

/** Startsida Anmälningsläge: activity fill rate vs `maxAllowedParticipants`. */
export enum ActivityOccupancyLevel {
  Low = 'low',
  Normal = 'normal',
  High = 'high'
}

export enum DanceRoles {
  Leader = 'leader',
  Follower = 'follower',
  Both = 'both',
  Single = 'single'
}

export type Child = {
  id: string
  name: string
  birthYear?: number
  createdAt: string
}

export type EnrollmentIndividualStatus = {
  activityId: string
  updatedAt: string
  status: EnrollmentStatus
}

export type EnrollmentRegisteredPartner = {
  activityId: string
  enteredName: string
  linkedUserName?: string
  linkedToUser?: string // ID of user from enrollment_users collection
}

// Convert to enum
export enum ActivityFilterOption {
  Active = 'active',
  Draft = 'draft',
  Archived = 'archived',
  All = 'all'
}

export type PaymentMethodFee = {
  method: PaymentMethod
  fee: number
}

export type ClientSwishConfig = {
  operator: string
  environment: SwishConfigEnvironment
}

export type ClientStipeConfig = {
  operator: string
  environment: StripeEnvironment
  paymentMethods: string[]
  /** Stripe Terminal reader ID (`tmr_…`) for this client's single pre-registered S700 walk-in reader. */
  terminalReaderId?: string
}

export type ClientVippsConfig = {
  operator: string
  environment: VippsConfigEnvironment
  integrationType: 'epayment' | 'checkout'
}

/** Fritidskortet (Swedish leisure subsidy) – operator key into fritidskortet_config collection. */
export type ClientFritidskortetConfig = {
  operator: string
  environment: 'test' | 'production'
}

export type DanceStyleColor = { name: string; color: string }

export type DropinPackage = {
  id: string
  name?: string
  isPopular: boolean
  isActive?: boolean // undefined or true = active (shown in checkout); false = inactive (hidden from checkout)
  numberOfClips: number
  pricePerClip: number // price in cents
  totalPrice: number // total price in cents
  colorCode?: string // Hex color for visual distinction (e.g. #A3D977)
  studentDiscount?: number // student discount amount in cents (e.g. 5000 = 50 SEK)
}

export type DropinDefaultExpiration = '6_months' | '1_year' | '2_years' | 'indefinite'

export type Currency = 'SEK' | 'NOK' | 'EUR'

// SMS Billing Types
// Stored in /clients/{clientId} - reference to SMS config
export type ClientSmsConfig = {
  clientId: string // Reference to client
  operator: string // e.g., 'Coursely' (for multi-tenant future)
}

// Stored in separate /sms_config/{clientId} collection (follows stripe_config pattern)
export type SmsConfig = {
  clientId: string
  allowance: number // Total SMS credits available (cumulative sum of all completed top-ups)
  topUpHistory: SmsTopUp[] // History of all purchases (first entry = billing start)
  subscriptionType?: 'adhoc' | 'subscription' // Payment model
  lowBalanceThreshold?: number // Optional: Alert when remaining SMS <= this value (e.g., 10)
  lowBalanceAlertSent?: boolean // Track if low balance alert email was sent (reset on top-up)
  limitReachedAlertSent?: boolean // Track if limit reached alert email was sent (reset on top-up)
  enabledEmailTypes: CommunicationLogEvent[] // Which email types trigger SMS (empty array = NO SMS sent)
  createdAt: string
  updatedAt: string
  // Note: 'used' is NOT stored - calculated real-time from communication_log
}

export type SmsTopUp = {
  id: string
  amount: number // Number of SMS credits purchased
  price: number // Price in cents (0 for initial grant)
  currency: Currency // 'SEK' | 'NOK' | 'EUR'
  purchasedAt: string // ISO timestamp (first entry = billing start date)
  purchasedBy: string // userId
  paymentMethod: 'stripe' | 'manual' | 'invoice' // Extensible for future payment methods
  paymentId?: string // Payment reference (Stripe payment ID, invoice number, etc.)
  type: 'adhoc' | 'subscription' // One-time purchase or recurring subscription
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'canceled'
  stripeMetadata?: {
    paymentIntentId?: string
    chargeId?: string
    refundId?: string
    receiptUrl?: string // Stripe receipt URL (available after successful payment)
  } // Stripe-specific metadata (undefined for manual/invoice entries)
}

// SMS usage statistics (calculated real-time, not stored)
export type SmsUsageStats = {
  allowance: number // Total SMS credits (sum of all completed top-ups)
  used: number // Total SMS sent (calculated from all communication_log entries)
  remaining: number // allowance - used (can be negative from historical over-use; new SMS is blocked at <= 0)
  unlimited: boolean // true if no SMS config exists (legacy behavior)
  lowBalanceThreshold?: number // Optional threshold for low balance alerts
  lowBalanceAlertSent?: boolean // Whether low balance alert has been sent
  limitReachedAlertSent?: boolean // Whether limit reached alert has been sent
}

export type Period = BaseObject & {
  periodId: string
  periodName: string
  periodStart: string // YYYY-MM-DD format
  periodEnd: string // YYYY-MM-DD format
  status: PeriodStatus
}

export type MembershipConfig = {
  scope: 'any_activity' | 'courses_only' | 'specific_activities'
  timeframe: 'same_period' | 'same_year' | 'anytime'
  specificActivityIds?: string[]
}

export type EmailProvider = 'sendgrid' | 'postmark'

/**
 * All available feature toggle keys
 * This is the SINGLE SOURCE OF TRUTH for feature toggles!
 *
 * To add a new feature toggle:
 * 1. Add the key to this array
 * 2. Add text keys to pure-js/assets/compact-texts.json:
 *    - editClientTabFeatures{FeatureName}Label
 *    - editClientTabFeatures{FeatureName}HelpText
 * 3. Done! The UI will automatically show the new toggle.
 */
export const CLIENT_FEATURE_TOGGLE_KEYS = [
  'dropinTickets',
  'privateLessons',
  'roomManagement',
  'receptionApp',
  'fortnoxTransactions',
  'sebTransactions',
  'sieConfig',
  'paymentMethodAdministrationFee',
  'registrationAppDownload',
  'hardCoupleIndividualParticipantFlow',
  'guestDancer',
  'fritidskortetPaymentApi',
  'epassiPayment',
  'extendedActivityTypes',
  'approvalEmailCheck',
  'receptionSwishQuickPayment',
  'receiptPrinter',
  'discountsAdvancedV2',
  'discountGroups',
  'receptionStripeTerminalWalkIn',
  /**
   * Default false ⇒ phone required by default (req. 10). Set true to allow phoneless
   * ("anonymous") Card walk-ins for this client — see EnrollmentUser.isAnonymousWalkIn.
   */
  'receptionStripeWalkInAllowAnonymous',
  /**
   * Card payment (Stripe Terminal S700) for an **existing** enrollment via Reception's
   * Trigger Pay (COU-325). No longer independently gated from `receptionStripeTerminalWalkIn`:
   * Edit Client keeps both flags in sync; readiness treats either as Card on.
   */
  'receptionStripeTerminalEnrollment',
  /**
   * Default false. When true, Edit Client → Advanced shows Customizable Transactional Emails
   * and school-notify flows (e.g. private lesson purchase) may send via the generic shell.
   */
  'customizableTransactionalEmails'
] as const

/**
 * Type derived from the keys array - no need to maintain separately!
 */
export type ClientFeatureToggles = {
  [K in (typeof CLIENT_FEATURE_TOGGLE_KEYS)[number]]?: boolean
}

export type SIEFiscalYear = {
  rar: number
  start: string // YYYY-MM-DD format
  end: string // YYYY-MM-DD format
}

export type SIEDimension = {
  id: number
  name: string
  defaultObject: string
}

export type SIERounding = {
  mode: string
  decimals: number
}

export type SIEAccount = {
  number: number
  name: string
  group: string
}

export type SIEConfig = {
  version: number
  encoding: string
  series: string
  fiscalYears: SIEFiscalYear[]
  accounts: {
    bank: number
    sales: Record<string, number>
    vat: Record<string, number>
  }
  dimensions: SIEDimension[]
  rounding: SIERounding
  accountList?: SIEAccount[] // Editable list for UI
}

/** Optional sections on the public activity detail page (name, description, and register CTA are always shown). */
export type ActivityPageFieldVisibility = {
  /** Venue name and street address */
  showLocation: boolean
  /** Room / studio (activity.place) */
  showRoom: boolean
  /** “Open in Google Maps” and the embedded map */
  showGoogleLinkAndMap: boolean
  /** Dates and times for each session */
  showOccasions: boolean
  /** Related activities and events tabs */
  showOtherActivitiesAndEvents: boolean
  /** Price and applicable discounts */
  showPrice: boolean
  /** Drop-in / at-door price (activity.priceAtDoor) */
  showDropinPrice: boolean
  /** Instructor names */
  showInstructors: boolean
}

/** Logo uploaded for a client portal. Stored at a stable path, so `updatedAt` doubles as a cache-buster. */
export type PortalLogo = {
  /** Public CDN url, e.g. https://media.coursely.se/portal/{clientId}/logo.png */
  url: string
  /** Storage object path, e.g. portal/{clientId}/logo.png. Kept so a replacement can delete the old object. */
  path: string
  width: number
  height: number
  contentType: string
  /** ISO timestamp of the last upload. */
  updatedAt: string
}

export type PortalConfig = {
  /** When false, the portal falls back to default Coursely branding even if the other fields are set. */
  enabled: boolean
  /** Shown in place of the default "Portal" wordmark. */
  name?: string
  /** Hex color, e.g. '#ef4444'. */
  primaryColor?: string
  logo?: PortalLogo
}

export type Client = BaseObject & {
  name: string
  slug?: string // URL-friendly identifier for SEO URLs (e.g., "stockholm-salsa" for /{slug}/activity/{activitySlug})
  address: string
  postalAddress?: string
  postalNumber?: string
  contactEmail: string
  bankGiro: string
  homepage: string
  orgNo: string
  logoUrl: string // deprecated, will use logoFullUrl
  logoFullUrl: string
  locations: Location[]
  typeTranslations?: Record<string, string>
  defaultVAT: VATRate
  /**
   * @deprecated Use SMS_Config.enabledEmailTypes instead. This field is kept for backward compatibility only.
   * To enable SMS for registration, add CommunicationLogEvent.ENROLLMENT_REGISTERED to SMS_Config.enabledEmailTypes.
   */
  sendSMSonRegistration: boolean
  importantAnnouncement?: string
  swishForetagNumber?: string
  showSsnInRegistration?: boolean
  showAddressInRegistration?: boolean
  /**
   * When true, approved participants see Open QR code on registration Step 4 and Step 5
   * before payment is complete. Reception check-in still requires payment.
   * Default false when absent. Formerly `featureToggles.registrationQrBeforePayment`.
   */
  showQrCodeBeforePayment?: boolean
  /**
   * Optional final URL opened in a new tab after successful payment in the register flow.
   * Used at the end of `/register/:activityId` flow before showing the final summary.
   */
  registerFlowFinalUrl?: string
  /**
   * Optional Google Tag Manager container ID (GTM-XXXXXXX).
   * When set, user-web loads the school’s GTM container on the course registration flow only.
   */
  gtmId?: string
  /**
   * When true, registrants in the register flow (Swish step) can change the amount to pay (partial Swish).
   * Default: unset/false — amount is fixed to the remaining balance (read-only in the UI).
   */
  registerFlowEditableSwishAmount?: boolean
  status: 'active' | 'inactive' | undefined
  /** SuperAdmin: Coursely pilot school. Suggested subscription price subtracts 50 SEK / month. */
  isPilotCustomer?: boolean
  paymentMethodFees: PaymentMethodFee[]
  discounts: Discount[]
  swishEnvironment?: SwishConfigEnvironment // deprecated, will use "swish" object
  swish?: ClientSwishConfig
  stripe?: ClientStipeConfig
  vipps?: ClientVippsConfig
  fritidskortet?: ClientFritidskortetConfig
  epassi?: ClientEpassiConfig
  fortnox?: ClientFortnoxConfig
  seb?: ClientSebConfig
  sms?: ClientSmsConfig
  currency?: Currency
  country?: string // ISO country code (e.g., 'SE', 'NO', 'DK') - defaults to 'SE' if not set
  hideDiscountsInPublicPage?: boolean
  /** Optional blocks on the public activity detail page (see ActivityPageFieldVisibility). */
  activityPageFieldVisibility?: Partial<ActivityPageFieldVisibility>
  themeConfig?: {
    backgroundColor: string
    borderColor: string
    textColor: string
    primaryColor: string
    secondaryColor: string
    activityBoxBorder: string
    activityBoxBackground: string
    activityHover: string
    courseBGColor: string
    occationBGColor: string
    otherBGColor: string
    // Nytt för dansstilar
    danceStyles?: DanceStyleColor[] // t.ex. [ { name: "salsa", color: "#A3D977" }, ... ]
    // Dark mode preset for integration component
    darkModePreset?: string // Values: 'default' (white), 'dark-gray', 'charcoal', 'black', 'navy'
  }
  /**
   * Branding for the client-specific portal reached via subdomain/custom domain (COU-334).
   * Separate from `themeConfig`, which styles the public activities page.
   * Ignored unless `enabled` is true — the unified portal always uses Coursely defaults.
   */
  portalConfig?: PortalConfig
  openActivityInNewTab?: boolean
  showFilters?: boolean
  allowBothForCouples?: boolean
  /**
   * When true, the client may offer paying all grouped enrollments in one payment (`payForGroup` / `payForGroup*`).
   * Supported methods include Stripe, Swish, and others — not Stripe-only. Default false when absent.
   */
  allowPayForBoth?: boolean
  emailAlias?: string
  defaultEmailContent?: string
  emailService?: EmailService
  emailConfig?: {
    fromEmail: string // e.g., 'info@salsadans.com' - shared across all providers
    fromName: string // e.g., 'Salsadans Stockholm' - shared across all providers
    trustedBy: Record<EmailProvider, boolean> // domain verification status per provider
  }
  emailNotifications?: {
    registrationNotification?: boolean
    /** School notify on private lesson purchase; default true when unset. */
    privateLessonPurchaseNotification?: boolean
  }
  activityTypesSupported: ActivityType // Bitwise sum of ActivityType flags, ie. ActivityType.Individual | ActivityType.Couples
  regions?: string[] // eg. Global, Nordic, Stockholm
  membershipConfig?: MembershipConfig
  dropinPackages?: DropinPackage[]
  dropinDefaultExpiration?: DropinDefaultExpiration
  freeDropinTicketRules?: FreeDropinTicketRule[] // COU-301: rules granting free drop-in tickets on qualifying registrations
  privateLessonPackages?: PrivateLessonPackage[]
  privateLessonValidityMonths?: number // Number of months a private lesson package is valid after purchase. Defaults to 12 if not set.
  periods?: Period[]
  subscriptionId?: string
  subscriptionStripeLink?: string
  featureToggles?: ClientFeatureToggles
  /**
   * Default automatic reminder configuration applied to newly created activities.
   * Each list value triggers a separate automated email send on the corresponding day.
   * Only applied when the `automatedReminderEmails` feature toggle is enabled.
   */
  automatedReminderDefaults?: {
    paymentReminderDaysBeforeList?: number[]
    paymentReminderDaysAfterFirstOccasionList?: number[]
    activityStartsSoonDaysBeforeList?: number[]
  }
  sieConfig?: SIEConfig
  helpDancerByEnrollmentUserId?: Record<string, HelpDancerConfig>
  helpDancerEnrollmentUserIds?: string[]
  /** Admin-configured SMS text templates per scenario. Key = CommunicationLogEvent value (e.g. 'dropin_payment_confirmed') */
  smsTextTemplates?: Record<string, Record<'sv' | 'en' | 'no', string>>
  /**
   * AI configuration for this school.
   * Used to personalize AI-generated activity descriptions.
   */
  aiConfig?: {
    /** A description of what the school is about — used as the AI system prompt. */
    systemPrompt?: string
    /** The school's brand voice, e.g. "warm and encouraging", "high-energy and bold". */
    brandVoice?: string
  }
  /** IP address of the Epson receipt printer on the local network (e.g. '192.168.0.105'). */
  printerIp?: string
  /** How the iPad kiosk reaches the Epson printer. Defaults to Wi-Fi when omitted. */
  printerConnectionMode?: PrinterConnectionMode
  /**
   * Named receipt printers for this school. Keyed by printer id.
   * When present, `printerIp` / `printerConnectionMode` mirror the default printer.
   */
  printers?: Record<string, ClientPrinter>
  /** Id of the standard printer in `printers`. Unassigned Checkin Screens and desktop prints use this. */
  defaultPrinterId?: string
  /** When false, Checkin App does not auto-print. Omitted or true = print. */
  printerPrintOnCheckin?: boolean
  /**
   * Manual sort order for activity list groups.
   * Outer key = groupBy field name (e.g. "period", "city", "style").
   * Inner key = group key (e.g. periodId, city string).
   * Value = sort index (lower = first).
   */
  styles?: string[] // Known dance styles for this client (used for autocomplete suggestions)
  levels?: string[] // Known activity levels for this client (used for autocomplete suggestions)
  tags?: string[] // Known tags for this client (used for autocomplete suggestions)
  cities?: string[] // Known cities for this client (used for autocomplete suggestions)
  groupSortOrders?: Record<string, Record<string, number>>
  /**
   * When true, the dashboard enrollment query filters out import enrollments (isImportEnrollment === true)
   * at the Firestore level. Only enable after all existing enrollments for this client have been
   * backfilled with the isImportEnrollment field.
   */
  hasImportEnrollmentFiltering?: boolean
  /**
   * Activity IDs hidden from the Reception kiosk “today” schedule. Omitted or empty = all visible.
   */
  receptionKioskHiddenActivityIds?: string[]
  /**
   * Local calendar day (`YYYY-MM-DD`) for which `receptionKioskPastGraceActivityIds` applies.
   * When not equal to today, pins are ignored (daily reset without a cron).
   */
  receptionKioskPastGraceLocalDay?: string
  /**
   * Activity IDs pinned to stay on the kiosk schedule after scheduled end + grace until that day ends (see `receptionKioskPastGraceLocalDay`).
   */
  receptionKioskPastGraceActivityIds?: string[]
  /**
   * Kiosk UI school-wide defaults. Reception prefers this over device localStorage; a device’s
   * `receptionKioskDevices[id].settings` overlays these fields when set.
   */
  receptionKioskSettings?: ReceptionKioskSettings
  /**
   * Named Checkin Screen configurations created by staff. Keyed by config id (not hardware).
   * Reception identity is `?kiosk=<id>` for an id that already exists here.
   * Sparse `settings` overlay school-wide `receptionKioskSettings`.
   */
  receptionKioskDevices?: Record<string, ReceptionKioskDevice>
  /**
   * Ephemeral signal from Check-in Control: Reception consumes this and starts walk-in pay
   * (student modal + Swish/card) for the activity. Optional `targetDeviceId` limits consume to one kiosk.
   */
  receptionKioskWalkInTrigger?: ReceptionKioskWalkInTrigger
  /**
   * When true, the custom conditions HTML is shown to registrants during the registration flow.
   */
  customConditionsEnabled?: boolean
  /**
   * HTML content for custom terms/conditions shown during registration.
   * Only displayed when customConditionsEnabled is true.
   */
  customConditions?: string
  /**
   * Custom URL to override the default Coursely conditions link shown in the registration flow
   * and sent as a parameter in registration emails. Falls back to the Coursely conditions page if not set.
   */
  customConditionsUrl?: string
  /**
   * Custom URL to override the default Coursely privacy policy link shown in the registration flow
   * and sent as a parameter in registration emails. Falls back to the Coursely privacy policy page if not set.
   */
  customPrivacyUrl?: string
}

/** Kiosk language override for Reception (maps to GrooverText / Language). */
export enum ReceptionKioskLanguage {
  Sv = 'sv',
  En = 'en',
  No = 'no'
}

export enum ReceptionKioskOrientation {
  Landscape = 'landscape',
  Portrait = 'portrait'
}

export enum ReceptionPointerPosition {
  Top = 'top',
  Bottom = 'bottom',
  Left = 'left',
  Right = 'right'
}

/** Written by Check-in Control “Pay”; cleared by Reception after handling. */
export type ReceptionKioskWalkInTrigger = {
  requestToken: string
  activityId: string
  requestedAt: string
  /** When set, Reception skips the student Yes/No modal and uses this for Swish pricing. */
  isStudent?: boolean
  /** When set, only this kiosk consumes the trigger. Omitted = legacy broadcast. */
  targetDeviceId?: string
}

export type ReceptionKioskSettings = {
  /** When true, use external barcode scanner flow instead of camera QR. */
  externalScannerMode?: boolean
  /**
   * When false, the floating “Camera” pointer overlay is hidden on the Reception kiosk.
   * Camera mode can stay on; defaults to true when omitted (legacy: show whenever pointer mode is on).
   */
  cameraIndicatorVisible?: boolean
  /** Pointer overlay mode (mirrors localStorage receptionPointerMode). */
  pointerModeEnabled?: boolean
  /** Active pointer position when pointer mode is on. */
  pointerPosition?: ReceptionPointerPosition
  /** UI language for Reception when kiosk override is active. */
  language?: ReceptionKioskLanguage
  /** Layout orientation for Reception shell. */
  orientation?: ReceptionKioskOrientation
  /** When false, left scanner / QR column is hidden. */
  showCameraPanel?: boolean
  /** When true, room/place name is shown right-aligned in Reception schedule rows. Defaults to false. */
  showRoomInReception?: boolean
  /**
   * When false, the “I want to register” CTA is hidden on the Reception schedule.
   * Defaults to true when omitted (legacy: always visible).
   */
  showRegisterCta?: boolean
  /** When true, shows the raw/normalised barcode-scan debug panel on the Reception kiosk — a
   * per-device troubleshooting aid, toggleable remotely from Check-in Control. Defaults to false. */
  scannerDebugEnabled?: boolean
}

/**
 * One admin-created Checkin Screen configuration. The key/`id` is the config id, not hardware.
 * Reception only heartbeats `lastSeenAt` for an id that already exists on the client.
 */
export type ReceptionKioskDevice = {
  id: string
  /** Staff-facing label (Check-in Control). When omitted, UI shows a fallback from `id`. */
  displayName?: string
  /** Set when Reception heartbeats this config. Omitted until first seen. */
  lastSeenAt?: string
  createdAt: string
  /** Sparse overlay: set fields win over school-wide `receptionKioskSettings`. */
  settings?: ReceptionKioskSettings
  /** Assigned printer id from `Client.printers`. Omitted = school default printer. */
  printerId?: string
}

/** Last Checkin App QR overlay mirrored into Control Center (`reception_live/lastScan`). */
export enum ReceptionScanMirrorOutcome {
  Success = 'success',
  AlreadyCheckedIn = 'alreadyCheckedIn',
  Failure = 'failure'
}

export type ReceptionScanMirrorEvent = {
  id: string
  createdAt: string
  outcome: ReceptionScanMirrorOutcome
  /** Omitted when the scan never resolved a person (invalid QR, wrong school). */
  personName?: string
  activityName?: string
  kioskDeviceId?: string
  kioskDisplayName?: string
  /** Localized failure copy key (invalid QR, unpaid, drop-in errors). */
  errorTextKey?: string
}

export type UpsertClientParams = Pick<Client, 'id'> & Partial<Omit<Client, 'id'>>

export type Room = BaseObject & {
  name: string
  maxAllowedPeople: number
}

export type Location = BaseObject & {
  googleUrl: string
  name: string
  adress: string
  center: {
    lat: number
    lng: number
  }
  rooms?: Room[]
}

/**
 * Room bookings (Room Management / room calendar).
 * Persisted in the client's room-management Firestore subcollection.
 */
export enum RoomBookingType {
  Class = 'Class',
  Training = 'Training',
  Event = 'Event'
}

export enum RoomBookingStatus {
  Approved = 'Approved',
  Pending = 'Pending'
}

export type RoomBookingContact = {
  name: string
  email?: string
  phone?: string
}

export type RoomBooking = BaseObject & {
  clientId: string
  locationId?: string
  roomId: string
  type: RoomBookingType
  status: RoomBookingStatus
  title: string
  startDate: string // ISO date-time
  endDate: string // ISO date-time
  comment?: string
  participantUserIds: string[]
  contact: RoomBookingContact
  activityId?: string
  occasionId?: string // Links to OccationInstance.id for activity occasions
}

export enum ClientSubscriptionStatus {
  Active = 'active',
  Inactive = 'inactive'
}

export type SubscriptionInvoice = {
  id: string
  invoiceDate: string // ISO date string
  amount: number // Amount in cents
  invoiceNumber?: string // Optional invoice reference
  periodMonth?: number // Month (1-12) for the period this invoice covers
  periodYear?: number // Year for the period this invoice covers
  subscriptionName?: string // Subscription name for this invoice (allows different names per invoice)
  revenueType?: 'stripe_payment' | 'invoice' | 'reversed_invoice' // Type of revenue
  link?: string // Optional link to the invoice document
  comment?: string // Optional internal comment
}

export enum SubscriptionMode {
  Stripe = 'stripe',
  ManualInvoicing = 'manual_invoicing'
}

export type ClientPrivate = {
  clientId: string
  subscription: {
    id: string
    name: string
    monthlyValue: number
    status: ClientSubscriptionStatus
    historicInvoices?: SubscriptionInvoice[]
    usesSwishHandel?: boolean
    mode?: SubscriptionMode
    /**
     * Day of month (1-31) when Stripe recurring subscription is withdrawn.
     * Only applicable when mode is Stripe.
     */
    withdrawalDayOfMonth?: number
    comment?: string
  }
}

// Bitwise flags for activity types
export enum ActivityType {
  Legacy = 1, // 0001
  Individual = 2, // 0010
  Couples = 4, // 0100
  NewTypeForFuture = 8 // 1000
}

export type ActivityRegistrationOverrideMode = 'couple' | 'individual'

export type ActivityRegistrationOverride = {
  /**
   * Forces the registration flow for this specific activity.
   * - couple: requires mainUser + partnerUser (hard couple)
   * - individual: enforces individual registration rules and subsettings
   */
  mode: ActivityRegistrationOverrideMode
  individual?: {
    /**
     * When true, user must select role (Leader/Follower/Both) with validation.
     * When false, role selection UI is hidden and role is auto-set to DanceRoles.Single.
     */
    showRoleWithValidation?: boolean
    /**
     * Controls whether the Step 2 "register partner name" (soft partner) UI is available.
     */
    allowPartnerRegistration?: boolean
  }
}

// An activity is a course or an one-time event
export type Activity = BaseObject & {
  // MANDATORY
  clientId: string // The school that uses the system
  type: 'Course' | 'Occasion' | 'Event' | 'Other'
  status: ActivityStatus
  name: string
  description: string
  length: number // In minutes ex. 55 min
  price: number
  eventType?: 'SingleEvent' | 'Event' | 'Subevent' | 'Parentevent' | 'Bigevent'
  subeventParentId?: string // Reference to the parent activity if this is a subevent
  approvalRequired: boolean
  style: string
  discountIds: string[] // Discount ids (Discounts that apply to this activity; points to client's discounts)
  maxAllowedParticipants: number // How many activities can be made before the course is full
  period: string // ex: "Q4 2024" - legacy field, kept for backward compatibility
  periodId?: string // Reference to Period object
  startDate: string // precise date (YYYY-MM-DD) when the course starts
  noOfOccasions: number // 10 for a course
  time: string
  vat: VATRate

  // OPTIONAL
  /**
   * Manual sorting index for ordering activities per client.
   * Used in admin Dashboard “Manual sorting” and in the public integration view (iframe) when groupSorting=manual.
   *
   * Note: Some views treat subevents as ordered within their parent; values can be assigned to preserve that
   * (e.g., parentIndex + childOffset).
   */
  manualSortIndex?: number
  instructorsNames?: string // List of teacher ids e.g userIds
  registrationStatus?: ActivityRegistrationStatus
  occations?: OccationInstance[]
  endDate?: string // precise date (YYYY-MM-DD) when the activity ends; defaults to startDate, can be overridden
  endTime?: string // end time (HH:mm); kept in sync with startDate/time/length, rolls to next day past midnight
  ageGroup?: string // ex. "18+"
  level?: string
  VAT?: number
  currency?: string
  discounts?: Discount[] // DEPRECATED Discounts that apply to this activity; use discountIds instead
  discountConfig?: {
    pricingConfigs: DiscountPricingConfig[]
    maxRegularSeats: number
  }
  regions?: string[] // List of regions supported for this activity (subset of Client.regions)
  place?: string // Text for now
  roomId?: string // Reference to Location.rooms[].id (overwrites place with room name in Edit Activity)
  city?: string
  locationId?: string // Reference to Location object
  maxAllowedUnbalance?: number // How many leaders/followers can be unbalanced
  /**
   * Optional per-role caps. When exceeded, new registrations are automatically queued (EnrollmentStatus.Pending).
   *
   * Note: These caps are evaluated in addition to maxAllowedParticipants/maxAllowedUnbalance rules.
   */
  maxAllowedLeaders?: number
  maxAllowedFollowers?: number
  singleRegistration?: boolean // If true, only one person can register
  substyle?: string
  colorCode?: string
  reserveListActive?: boolean
  allowDropin?: boolean
  /** When true: no registration, only drop-in check-ins. */
  dropinOnly?: boolean
  /** If set: only these drop-in packages can be used. If empty/undefined: any drop-in package. */
  allowedDropinPackageIds?: string[]
  group?: string // group similar courses (ie. Salsa1 for period1 and period2); also allows
  groupingCode?: string // Reference to other activities
  priceAtDoor?: number
  bannerUrl?: string
  allowChildRegistration?: boolean // Enable child registrations for this activity
  /**
   * When true, users cannot add/remove additional activities during registration.
   * (Step 2 activity selection becomes read-only; other Step 2 inputs can still be used.)
   */
  disableAdditionalActivities?: boolean
  /**
   * When true, this activity is shown at the top of the activity list
   * in the registration flow (e.g. Step 2 "Add more" list), when shown in the list.
   */
  pinToTopOfRegistrationList?: boolean
  /**
   * Optional activity-level override for the registration flow (couple vs individual + subsettings).
   *
   * NOTE: Firestore merge writes ignore `undefined` (field won't be removed). We use `null` to explicitly clear
   * a previously saved override object from the document.
   */
  registrationOverride?: ActivityRegistrationOverride | null
  /**
   * Days before first occasion to send payment reminder to approved + unpaid enrollments.
   * 0 or undefined = disabled. Default when enabled: 5.
   * @deprecated Use paymentReminderDaysBeforeList. Kept for backward compat (read-only after migration).
   */
  paymentReminderDaysBefore?: number
  /**
   * Days before first occasion to send "activity starts soon" email to all approved enrollment users.
   * 0 or undefined = disabled. Default when enabled: 1 (24h).
   * @deprecated Use activityStartsSoonDaysBeforeList. Kept for backward compat (read-only after migration).
   */
  activityStartsSoonDaysBefore?: number
  /**
   * Days before first occasion to send payment reminders. Each value triggers a separate send.
   * Replaces the scalar paymentReminderDaysBefore. Empty array = disabled.
   */
  paymentReminderDaysBeforeList?: number[]
  /**
   * Days after the first occasion to send payment reminders to approved, unpaid enrollments.
   * Each value triggers a separate send on that calendar day. Empty array or undefined = disabled.
   */
  paymentReminderDaysAfterFirstOccasionList?: number[]
  /**
   * Days before first occasion to send "activity starts soon" emails. Each value triggers a separate send.
   * Replaces the scalar activityStartsSoonDaysBefore. Empty array = disabled.
   */
  activityStartsSoonDaysBeforeList?: number[]
  onlySwishPayment?: boolean // If true, only show Swish as payment method (when available)
  swishQuickPaymentEnabled?: boolean // If true, allow direct Swish QR payment at reception (walk-in)
  /** When false, Checkin App does not auto-print for this activity. Omitted or true = print. */
  printOnCheckin?: boolean
  // SEO fields
  seoLink?: string // SEO-friendly URL slug for marketing purposes (auto-generated from name)
  seo?: ActivitySeo // Custom SEO metadata for this activity
  tags?: string[] // Custom tags for categorisation and filtering
  isImportActivity?: boolean // if true, all enrollments for this activity are treated as imported (excluded from customer statistics)
}

/**
 * SEO metadata for activities - allows customization of <head> meta tags
 */
export type ActivitySeo = {
  /** Custom <title> (overrides activity.name) */
  title?: string
  /** Custom meta description (overrides activity.description) */
  description?: string
  /** Meta keywords */
  keywords?: string[]
  /** Optional override GA tracking ID for this specific activity */
  gaTrackingId?: string
  /** Custom Open Graph image URL (overrides activity.bannerUrl for sharing) */
  image?: string
}

export type OccationInstance = {
  id: string
  startDate: string // ex. 2024-10-01 17:00
  endDate: string // ex. 2024-12-01 18:00
  instructors?: string[] // List of teacher ids e.g userIds
  index: number // 0-based occasion index (used as Checkin.occurenceNo)
  cancelled?: boolean // Whether this occasion is cancelled
  roomId?: string // Optional per-occasion room override (overrides Activity.roomId for this session)
  roomBookingId?: string // Direct link to the RoomBooking doc — canonical booking ID for this occasion
}

export type Checkin = BaseObject & {
  activityId: string
  enrollmentId: string
  userId: string
  occurenceNo: number
}

// used to send user data from frontend to backend
export type EnrollmentUserRequest = {
  createdAt?: string
  name: string
  email: string
  phone: string
  type: DanceRoles
  nonObfuscatedName?: string
  ssn?: string
  address?: string
  children?: Child[] // Array of children for this user (added for parent/child support)
  isParent?: boolean // Explicit parent flag (added for parent/child support)
}

export type HelpDancerLevel = 'beginner' | 'intermediate' | 'advanced'

export type HelpDancerConfig = {
  enabled: boolean
  level?: HelpDancerLevel
  approvedActivityIds?: string[]
  comment?: string
  updatedAt: string
  updatedByUserId?: string
}

export type EnrollmentUserRequestV2 = EnrollmentUserRequest & {
  // V2 explicitly includes children and isParent for clarity in versioned requests
  // These fields are also in the base EnrollmentUserRequest for backward compatibility
}

export type EnrollmentUserGetOrCreateRequest = Pick<EnrollmentUser, 'name' | 'email' | 'phone'>

export type EnrollmentUserGetOrCreateResponse = {
  enrollmentUserId: string
}

export type EnrollmentPaymentInputRequest = {
  user: 'mainUser' | 'partnerUser'
  method: PaymentMethod
}

export type EnrollmentRequestV3 = {
  clientId: string
  mainUser: EnrollmentUserRequest
  partnerUser?: EnrollmentUserRequest
  activityIds: string[]
  activityQuantities?: Record<string, number> // activityId -> quantity
  comment?: string
  discountUserInput: DiscountUserInputV3
  paymentInput: EnrollmentPaymentInputRequest[]
  env: string
  childrenByActivity?: Record<string, string[]> // activityId -> childId[]
}

export type EnrollmentRequestResponseV3 = {
  enrollmentId: string
}

export type EnrollmentRequestResponseV4 = {
  enrollmentId: string
}

export type UserEnrollmentPeriod = {
  id: string
  name: string
  period: string
  activityType: string
}

export enum CreditRecordType {
  Add = 'add',
  Pay = 'pay',
  Remove = 'remove'
}

export type CreditRecordHistory = BaseObject & {
  amount: number
  type: CreditRecordType
  action: 'enrollment' | 'admin'
  actionInfo?: string
  activityId?: string
}

// database representation of a user in a course
export type EnrollmentUser = BaseObject &
  Omit<EnrollmentUserRequest, 'type'> & {
    stripeCustomerId?: Record<string, string>
    creditData?: Record<string, CreditRecordHistory[]>
    children?: Child[] // Array of children for this user
    isParent?: boolean // Explicit parent flag
    /** Per-client anonymous Stripe walk-in sentinel (`anon_walkin_<clientId>`), `phone: ''`. Absent on all real users. */
    isAnonymousWalkIn?: boolean
    /** Storage path of the customer's profile image, e.g. `enrollmentUserImages/<64 hex>.jpg`.
     *  Never a URL — the object is auth-gated and read via getBlob(). Empty/absent = no image. */
    profileImagePath?: string
    /** Set while an admin phone re-key is in progress (`changeEnrollmentUserPhone`); cleared when the old doc is deleted. */
    rekeyedFromId?: string
  }

export enum EnrollmentPaymentStatus {
  Unpaid = 'unpaid',
  PartlyPaid = 'partlyPaid',
  Paid = 'paid',
  Refunded = 'refunded',
  PartlyRefunded = 'partlyRefunded'
}

export type Enrollment = BaseObject & {
  version?: number
  clientId: string
  mainUser: (EnrollmentUserRequest | EnrollmentUserRequestV2) & { id: string }
  partnerUser?: (EnrollmentUserRequest | EnrollmentUserRequestV2) & { id: string }
  activities: Activity[]
  activityIds?: string[] // this is populated automatically from activities in enrollmentUpsert(); makes db queries faster
  activityQuantities?: Record<string, number> // activityId -> quantity (walk-in: SingleEvent or ordinary Event)
  status: EnrollmentStatus
  statuses?: EnrollmentIndividualStatus[]
  /** Payment/reference OCR; always create via `generateOcr()` / `generateOcrWithSuffix()` (see `Ocr` in OcrTypes). */
  ocr: Ocr
  discountUserInput: DiscountUserInputV1 | DiscountUserInputV2 | DiscountUserInputV3
  payments: Payment[] // payments are future operations when doing payments or marking as paid
  paymentStatus: EnrollmentPaymentStatus
  priceDetails: PriceDetails[] // Breakdown of the price
  checkins?: Checkin[]
  logs?: EnrollmentAuditLog[] // Audit logs for enrollment changes (e.g., payment deletions)
  comment?: string
  currency?: string
  discountRegion?: string // String identifier for the discount type (e.g., 'nordic', 'global')
  DiscountRegistrationId?: string
  childrenByActivity?: Record<string, string[]> // activityId -> childId[] mapping
  /** When true this enrollment was created via a bulk import and should be excluded from customer-facing statistics. */
  isImportEnrollment?: boolean
  /**
   * Groups enrollments created in the same registration transaction (e.g. hard-couple split).
   * Not used as a primary key; use `id` for document identity.
   */
  enrollmentGroupId?: string
}

/** Subset of Enrollment used only for walk-in row display + Swish amount fallback in drop-in check-in merge. */
export type EnrollmentWalkInMerge = {
  id: string
  mainUser: { id: string; name: string; phone?: string }
  payments: Payment[]
}

export type EnrollmentPaymentDetails = {
  source: PaymentSource
  paymentDate: string
  paymentMethod: string
  paymentRef: string
  paymentOperator: string
  paymentEnvironment?: string
  paymentInfo?: string
}

export type EnrollmentWithPayment = Enrollment & {
  paymentDetails: EnrollmentPaymentDetails
}

// keep only data safe to publish in public
export type EnrollmentPublic = Pick<Enrollment, 'id' | 'createdAt' | 'paymentStatus' | 'status' | 'ocr'> & {
  clientId?: string
  updatedAt: string
}

// if refund, value is negative
export type Payment = {
  currency: string
  totalAmount: number // Including VAT
  paymentDate?: string // TODO remove, use stripe payment object instead
  paymentRef?: string // ex. Stripe payment id or Swish instructionUUID
  swishPaymentReference?: string // generated by Swish when creating payment and returned in payment object
  paymentMethod: string // PaymentMethod
  paymentInfo?: string // ex. Stripe card last4 digits
  userId?: string // userId of the user that created the enrollment
  source: PaymentSource
  type: 'pay' | 'refund'
  paymentOperator?: string
  paymentEnvironment?: string
  originalPaymentReference?: string // Reference to the original payment for refunds
}

export type EnrollmentAuditLog = {
  event: 'payment_deleted' | 'payment_added'
  performedBy:
    | {
        userId: string
        userEmail: string
      }
    | 'system'
  payment: Payment
  createdAt: string
}

export enum EnrollmentChangeLogEvent {
  StatusChanged = 'status_changed',
  PriceChanged = 'price_changed',
  CommentChanged = 'comment_changed'
}

export type EnrollmentChangeLogActor =
  | {
      userId: string
      userEmail: string
    }
  | 'system'

export type EnrollmentChangeLogChange = {
  field: string
  oldValue: unknown
  newValue: unknown
}

/** Append-only row in `clients/{clientId}/enrollments/{id}/enrollment_logs`. */
export type EnrollmentChangeLog = {
  id: string
  event: EnrollmentChangeLogEvent
  performedBy: EnrollmentChangeLogActor
  changes: EnrollmentChangeLogChange[]
  createdAt: string
}

export type PriceDetails = {
  activityId: string
  originalPrice: number // price before discount; course price
  totalDiscountPrice: number // discount price from all discounts
  finalPrice: number // originalPrice - totalDiscountPrice
  paymentMethod: PaymentMethod
  administrationFee: number // derives from the payment method selected
  discounts?: Discount[] // Discounts used
  discountIds?: string[] // id of discounts used
  appliesTo: string // which user the price applies to (mainUser or partnerUser)
  manualDiscountComment?: string // comment for manual discount adjustments
}

export type EmailTemplate = BaseObject & {
  clientId: string
  name: string
  subject: string
  body: string
  replyToEmail: string
  replyToName: string
}

export enum PayFor {
  Both, // = 0
  Main, // = 1
  Partner // = 2
}

// Branded type to prevent passing raw numbers as PayFor enum
export type PayForEnum = PayFor & { readonly __payForBrand: unique symbol }

export type VatData = Record<number, number>

export type UrlShortHash = {
  url: string
  createdAt: string
}

export type EarlyBirdBooking = {
  id?: string
  activityId: string
  enrollmentId?: string
  enrollmentUserId: string
  registrationType: DiscountPricingConfig['registrationType']
  region: string
  status: 'pending' | 'confirmed' | 'cancelled'
  price: number // The price that was offered at registration time
  pricingConfigId?: string // ID of the specific pricing configuration used, if any (null for regular price)
  createdAt?: string
  updatedAt?: string
}

export enum DropinTicketStatus {
  PendingPayment = 'pending_payment',
  Active = 'active',
  Expired = 'expired',
  UsedUp = 'usedUp',
  Deleted = 'deleted',
  Refunded = 'refunded'
}

export enum DropinDiscountType {
  Student = 'student'
}

export enum DropinTicketSource {
  Purchase = 'purchase',
  FreeDropinReward = 'free_dropin_reward'
}

export type DropinTicket = BaseObject & {
  clientId: string
  enrollmentUserId?: string
  enrollmentUser: {
    id: string
    name: string
  }
  packageId?: string // reference to the DropinPackage
  packageName?: string // redundancy for display
  ticketCount: number // e.g. 5, 10, 20 tickets
  ticketsUsed: number // 0–ticketCount
  schoolName: string // redundancy for display
  price: number // final (effective) gross price in cents, after any discount
  basePrice: number // original package price in cents before discounts
  discountAmount?: number // discount applied in cents (e.g. 5000 = 50 SEK)
  discountType?: DropinDiscountType // type of discount applied
  vat: VATRate
  validUntil: string // ISO, 6 eller 12 månader efter köp
  paymentMethod: PaymentMethod
  paymentOperator?: string // payment operator (e.g., 'Coursely', 'Kursanmalan')
  paymentEnvironment?: string // payment environment (e.g., 'test', 'production')
  status: DropinTicketStatus
  source?: DropinTicketSource // undefined on legacy purchased tickets
  freeDropinRewardProvenance?: FreeDropinTicketProvenance
  comment?: string // internal admin note; not rendered in any user-facing UI, but readable via Firestore SDK — do not store sensitive data
  logs: DropinTicketLog[] // embedded usage log (first entry is purchase log)
}

export type DropinTicketLog = {
  id: string
  timestamp: string
  type: 'purchase' | 'consume' | 'statusUpdate' | 'refund'
  message?: string
  clientId?: string
  clientName?: string // schoolName
  refundAmount?: number // refund amount in cents (öre)
  refundReason?: string // reason for refund
  /** For type === 'consume': activity where ticket was used. */
  activityId?: string
  /** For type === 'consume': occasion (OccationInstance.id) where ticket was used. */
  occasionId?: string
}

export type DropinTicketPublic = BaseObject & {
  clientId: string
  clientName: string // client name for receipt display
  enrollmentUserId: string
  packageId?: string // reference to the DropinPackage
  packageName?: string // redundancy for display
  ticketCount: number // e.g. 5, 10, 20 tickets
  ticketsUsed: number // 0–ticketCount
  schoolName: string // redundancy for display
  price: number // final (effective) gross price in cents, after any discount
  basePrice: number // original package price in cents before discounts
  discountAmount?: number // discount applied in cents
  discountType?: DropinDiscountType // type of discount applied
  vat: VATRate
  validUntil: string // ISO, 6 eller 12 månader efter köp
  paymentMethod: PaymentMethod
  paymentOperator?: string // payment operator (e.g., 'Coursely', 'Kursanmalan')
  paymentEnvironment?: string // payment environment (e.g., 'test', 'production')
  status: DropinTicketStatus
  updatedAt?: string
}

export type PrivateLessonPackage = {
  id: string
  name?: string
  isPopular: boolean
  lessonCount: number // == numberOfClips
  durationMin: number // fixed per school (policy)
  pricePerLesson: number // in cents
  totalPrice: number // in cents
  vat?: VATRate // default to Client.defaultVAT if omitted
  active?: boolean
  isDeleted: boolean
}

// Reference types for instructor and location
export type InstructorReference = {
  id: string
  name: string
}

export type LocationReference = {
  id: string
  name: string
}

export enum PrivateLessonOrderStatus {
  PendingPayment = 'pending_payment',
  Active = 'active',
  Expired = 'expired',
  UsedUp = 'usedUp',
  Refunded = 'refunded',
  Deleted = 'deleted'
}

export type PrivateLessonOrder = BaseObject & {
  clientId: string
  // buyer
  enrollmentUserId?: string
  enrollmentUser: { id: string; name: string } // mirror DropinTicket
  // package snapshot
  packageId?: string // reference to the PrivateLessonPackage
  packageName?: string // redundancy for display
  durationMin: number
  lessonCount: number
  lessonsUsed: number
  validUntil?: string // ISO; optional if you don't expire packages
  // instructor (optional for buy-now-book-later)
  defaultInstructor?: InstructorReference // object with id and name
  defaultLocation?: LocationReference // object with id and name
  /** @deprecated Use defaultInstructor.id instead */
  instructorId?: string
  /** @deprecated Use defaultInstructor.name instead */
  instructorName?: string // redundancy for display
  // payment
  currency?: Currency
  price: number // total paid in cents
  paymentMethod: PaymentMethod
  paymentStatus: EnrollmentPaymentStatus // reuse existing enum
  payments?: Payment[] // payment history (Swish, etc.)
  status: PrivateLessonOrderStatus
  // bookkeeping
  schoolName?: string // redundancy for display
  registrationComment?: string // stored + emailed
  logs: PrivateLessonLog[] // same shape as DropinTicketLog
  updatedAt?: string
}

export type PrivateLessonLog = {
  id: string
  timestamp: string
  type: 'purchase' | 'consume' | 'refund' | 'statusUpdate' | 'reschedule'
  message?: string
  lessonId?: string
  actor?: 'system' | { userId: string; userEmail?: string }
}

// Scheduled lessons (occasion) derived from an order
export type PrivateLesson = BaseObject & {
  clientId: string
  orderId: string // reference to the PrivateLessonOrder
  enrollmentUserId: string
  instructor?: InstructorReference // object with id and name
  startDate: string // ISO
  endDate: string // ISO
  location?: LocationReference // object with id and name
  status: 'requested' | 'scheduled' | 'completed' | 'cancelled'
  attended: boolean
  /** @deprecated Use instructor.id instead */
  instructorId?: string // reference to the User
  /** @deprecated Use location.id instead */
  locationId?: string // studio or 'online'
}

// Blocked time periods for private lessons
export type PrivateLessonBlock = BaseObject & {
  clientId: string
  instructorIds?: string[] // If undefined or empty, block applies to all instructors
  startDate: string // ISO timestamp
  endDate: string // ISO timestamp
  /** Optional label shown in admin calendar / lists (e.g. vacation, studio maintenance). */
  reason?: string
  createdBy: string // User ID who created the block
}

export type PrivateLessonInstructor = {
  id: string
  name: string
  specialties: string[]
  profilePicture?: string
}

export type PrivateLessonConfig = {
  clientId: string
  clientName: string
  packages: PrivateLessonPackage[]
  instructors: PrivateLessonInstructor[]
  currency: Currency
  validityMonths: number // Number of months a purchased package is valid. Defaults to 12.
}

/**
 * Maps subdomains/custom domains to clients for SEO routing
 * Collection: client_host
 */
export type ClientHost = BaseObject & {
  /** Subdomain for danceschool.coursely.se */
  subdomain: string
  /** Optional custom domains (e.g., courses.danceschool.se) */
  customDomains?: string[]
  /** Reference to client */
  clientId: string
  /** Whether this host mapping is active */
  enabled: boolean
  /** Optional analytics configuration */
  analytics?: {
    /** Google Analytics 4 Measurement ID (e.g., "G-XXXXXXXXXX") */
    gaTrackingId?: string
  }
}

export type WeeklyData = {
  week: string
  newUsers: number
  returningUsers: number
}

export type UserMetricsData = {
  weeklyData: WeeklyData[]
  totalNewUsers: number
  totalReturningUsers: number
  totalInactiveUsers: number
  totalSuperCustomers: number
  totalUsers: number
}

/**
 * Cached customer metrics, stored at `clients/{clientId}/customer_metrics/current`.
 *
 * `computedAt` is an ISO 8601 *string*, not a Firestore Timestamp — matching the convention used by
 * `createdAt` on Enrollment, which the metrics computation already parses with `new Date(...)`.
 * Storing a Timestamp here would reintroduce exactly the string-vs-Timestamp ambiguity COU-338 had
 * to rule out while diagnosing the original bug.
 */
export type CustomerMetricsCacheDoc = UserMetricsData & {
  computedAt: string
}

// ─── Receipt Printer ────────────────────────────────────────────────────────

export enum PrinterConnectionMode {
  Wifi = 'wifi',
  Usb = 'usb'
}

/** One named Epson receipt printer on the school LAN. */
export type ClientPrinter = {
  id: string
  displayName?: string
  ip: string
  connectionMode?: PrinterConnectionMode
}

export type DropinReceiptData = {
  schoolName: string
  className: string
  dateTime: string
  studentName: string
  price: number
  currency: string
  paymentMethod: string
}

export type EpsonRasterImage = {
  width: number
  height: number
  data: string // base64-encoded 1-bit raster, MSB first, rows padded to bytes
}

export type CheckinReceiptData = {
  schoolName: string
  className: string
  studentName: string
  dateTime: string
  /** Studio / room (sal) when the activity has one. */
  roomName?: string
  logoImage?: EpsonRasterImage
  /** Later re-scan of an already-checked-in ticket — slip is a reprint, not a new check-in. */
  alreadyPrinted?: boolean
  /** Original check-in timestamp shown under ALREADY PRINTED. */
  previousCheckinAt?: string
}

export type CheckinPaperData = {
  schoolName: string
  className: string
  studentName?: string
  dateTime: string
  logoImage?: EpsonRasterImage
}

/** How a Control Center / reception slip was requested. Stored on `reception_print_logs`. */
export enum ReceptionPrintSource {
  Manual = 'manual'
}

/** Append-only log of extra slips printed from Check-in Control (`clients/{clientId}/reception_print_logs`). */
export type ReceptionPrintLog = {
  id: string
  clientId: string
  activityId: string
  createdAt: string
  source: ReceptionPrintSource
}
